import { apiService } from '../../../services/apiService';
import { API_PATHS } from '../../../utils/apiPaths';
import { extractApiData, isApiSuccess } from '../../../utils/helpers';
import type {
  GetRolesRequest,
  ProvisionUserRequest,
  RoleCallerType,
  RoleOption,
} from '../../roles/models/types';
import type {
  PlatformUserListResponse,
  PlatformUserUpdateRequest,
  RecentLogin,
  UserColumnFilters,
} from '../models/types';
import { EMPTY_USER_COLUMN_FILTERS } from '../constants/constants';
import {
  buildPlatformUserRequest,
  normalizeMerchants,
  normalizePlatformUsers,
  normalizeRoles,
} from '../utils/utils';

function resolveCallerType(adminType?: string): RoleCallerType {  
  const normalized = String(adminType ?? '').trim().toUpperCase();
  if (normalized === 'SUPER_ADMIN' || normalized.includes('SUPER')) return 'SUPER_ADMIN';
  if (normalized === 'ADMIN' || normalized.includes('SUB')) return 'SUB_ADMIN';
  return 'SUPER_ADMIN';
}

function normalizeRecentLogins(raw: unknown): RecentLogin[] {
  const list = Array.isArray(raw) ? raw : [];
  return (list as Record<string, unknown>[])
    .map((item) => ({
      ipAddress: String(item.ipAddress ?? '').trim() || 'Unknown IP',
      location: String(item.location ?? '').trim(),
      occurredAt: String(item.occurredAt ?? ''),
      status: String(item.status ?? '').trim(),
    }))
    .filter((item) => item.occurredAt.length > 0);
}

export async function fetchRolesList(
  adminType?: string,
): Promise<RoleOption[]> {
  const callerType = resolveCallerType(adminType);
  const request: GetRolesRequest = { callerType };        
  const response: unknown = await apiService.post(API_PATHS.GET_ROLES, request);
  if (!isApiSuccess(response)) {
    throw new Error((response as { message?: string })?.message || 'Unable to load roles.');
  }
  return normalizeRoles(extractApiData<unknown>(response));
}

export async function fetchPlatformUsers(options: {
  filterStatus: string;
  filterRoleId: string;
  search: string;
  columnFilters: UserColumnFilters;
  export?: boolean;
}): Promise<PlatformUserListResponse> {
  const response: unknown = await apiService.post(
    API_PATHS.GET_PLATFORM_USERS,
    buildPlatformUserRequest(options),
  );
  if (!isApiSuccess(response)) {
    throw new Error(
      (response as { message?: string })?.message || 'Unable to load platform users.',
    );
  }
  return extractApiData<PlatformUserListResponse>(response) ?? {};
}

export async function fetchStatsPlatformUsers(): Promise<PlatformUserListResponse> {
  return fetchPlatformUsers({
    filterStatus: 'All',
    filterRoleId: 'All',
    search: '',
    columnFilters: EMPTY_USER_COLUMN_FILTERS,
  });
}

export async function fetchActiveMerchants() {
  const response: unknown = await apiService.post(API_PATHS.GET_MERCHANTS_BY_STATUS, {
    status: 'ACTIVE',
  });
  if (!isApiSuccess(response)) {
    throw new Error((response as { message?: string })?.message || 'Unable to load merchants.');
  }
  return normalizeMerchants(extractApiData<unknown>(response));
}

export async function provisionUser(payload: ProvisionUserRequest) {
  const response: unknown = await apiService.post(API_PATHS.PROVISION_USER, payload);
  if (!isApiSuccess(response)) {
    throw new Error((response as { message?: string })?.message || 'Unable to provision user.');
  }
}

export async function updatePlatformUser(payload: PlatformUserUpdateRequest) {
  const response: unknown = await apiService.post(API_PATHS.UPDATE_PLATFORM_USER, payload);
  if (!isApiSuccess(response)) {
    throw new Error((response as { message?: string })?.message || 'Unable to update user.');
  }
}

export async function impersonateUser(userId: number, userType: string) {
  const response: unknown = await apiService.post(API_PATHS.IMPERSONATE_USER, {
    userId,
    userType,
  });
  if (!isApiSuccess(response)) {
    throw new Error(
      (response as { message?: string })?.message || 'Unable to start impersonation session.',
    );
  }
  return extractApiData<Record<string, unknown>>(response) ?? {};
}

export async function fetchRecentLogins(userId: number, userType: string): Promise<RecentLogin[]> {
  const response: unknown = await apiService.post(API_PATHS.GET_USER_RECENT_LOGINS, {
    userId,
    userType,
  });
  if (!isApiSuccess(response)) {
    throw new Error((response as { message?: string })?.message || 'Unable to load recent logins.');
  }
  return normalizeRecentLogins(extractApiData<unknown>(response));
}

export { normalizePlatformUsers };
